import React, { useCallback, useEffect, useState } from 'react';
import {
    Button,
    Card,
    message,
    Popconfirm,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography,
} from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';

import MyAppsAPI from '../api/myapps';
import MyAppForm from './MyAppForm';
import SecretRevealModal from './SecretRevealModal';

const { Text } = Typography;

const STATUS_COLOR = {
    pending: 'orange',
    approved: 'green',
    rejected: 'red',
};

function formatDate(s) {
    if (!s) return '';
    try {
        return new Date(s).toLocaleString();
    } catch (e) {
        return s;
    }
}

function extractRejectionReason(notes) {
    if (!notes) return null;
    const parts = notes.split('\n').map((s) => s.trim());
    const last = [...parts].reverse().find((p) => p.startsWith('REJECTED'));
    return last || null;
}

export default function MyApps() {
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [secret, setSecret] = useState(null);

    const refresh = useCallback(() => {
        setLoading(true);
        MyAppsAPI.list(
            (res) => {
                setApps(res || []);
                setLoading(false);
            },
            (err) => {
                console.log(err);
                setLoading(false);
                message.error('Failed to load apps');
            }
        );
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const openCreate = () => {
        setEditing(null);
        setFormOpen(true);
    };

    const openEdit = (row) => {
        setEditing(row);
        setFormOpen(true);
    };

    const handleSubmit = (payload) => {
        setSaving(true);
        if (editing) {
            MyAppsAPI.update(
                editing.app_id,
                payload,
                () => {
                    setSaving(false);
                    setFormOpen(false);
                    setEditing(null);
                    message.success('Updated');
                    refresh();
                },
                (err) => {
                    setSaving(false);
                    message.error(err?.detail || 'Update failed');
                }
            );
        } else {
            MyAppsAPI.create(
                payload,
                (res) => {
                    setSaving(false);
                    setFormOpen(false);
                    setSecret({ app_id: res.app_id, app_secret: res.app_secret });
                    message.success('Registered — waiting for admin approval');
                    refresh();
                },
                (err) => {
                    setSaving(false);
                    message.error(err?.detail || 'Registration failed');
                }
            );
        }
    };

    const handleRotate = (row) => {
        MyAppsAPI.rotateSecret(
            row.app_id,
            (res) => {
                setSecret({ app_id: res.app_id, app_secret: res.app_secret });
                refresh();
            },
            (err) => {
                message.error(err?.detail || 'Rotation failed');
            }
        );
    };

    const handleDelete = (row) => {
        MyAppsAPI.remove(
            row.app_id,
            () => {
                message.success('Deleted');
                refresh();
            },
            (err) => {
                message.error(err?.detail || 'Delete failed');
            }
        );
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'app_name',
            render: (v, row) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{v}</Text>
                    <Text type="secondary" copyable style={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {row.app_id}
                    </Text>
                </Space>
            ),
        },
        {
            title: 'Service URL(s)',
            dataIndex: 'service_url',
            render: (v) => (
                <div style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12 }}>
                    {(v || '').split('::').join('\n')}
                </div>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            render: (s, row) => {
                const tag = <Tag color={STATUS_COLOR[s] || 'default'}>{s}</Tag>;
                if (s === 'rejected') {
                    const reason = extractRejectionReason(row.notes);
                    return <Tooltip title={reason || 'Rejected'}>{tag}</Tooltip>;
                }
                return tag;
            },
        },
        {
            title: 'Expires',
            dataIndex: 'expire_time',
            render: formatDate,
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, row) => (
                <Space wrap>
                    <Button size="small" onClick={() => openEdit(row)}>Edit</Button>
                    <Button
                        size="small"
                        disabled={row.status !== 'approved'}
                        onClick={() => handleRotate(row)}
                    >
                        Rotate Secret
                    </Button>
                    <Popconfirm
                        title="Delete this app?"
                        description="Issued tickets will continue to validate until they expire."
                        onConfirm={() => handleDelete(row)}
                    >
                        <Button size="small" danger>Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <>
            <Card
                title="My Applications"
                extra={
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading}>
                            Refresh
                        </Button>
                        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                            Register App
                        </Button>
                    </Space>
                }
            >
                <Text type="secondary">
                    Register an application to obtain an <code>app_id</code> / <code>app_secret</code>.
                    Requests require admin approval; this can take a few days. Use Refresh to see status changes.
                </Text>
                <Table
                    style={{ marginTop: 16 }}
                    rowKey="app_id"
                    columns={columns}
                    dataSource={apps}
                    loading={loading}
                    pagination={false}
                />
            </Card>

            <MyAppForm
                open={formOpen}
                initial={editing}
                confirmLoading={saving}
                onSubmit={handleSubmit}
                onCancel={() => { setFormOpen(false); setEditing(null); }}
            />

            <SecretRevealModal
                open={!!secret}
                data={secret}
                onClose={() => setSecret(null)}
            />
        </>
    );
}
